// https://github.com/patriciogonzalezvivo/glslEditor

import React, { useRef, useEffect, useState } from "react";
import GlslCanvas from "glslCanvas";

const ShaderCanvas = ({ fragShader, onFrame }) => {
  const canvasRef = useRef(null);
  const sandboxRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const mouseRef = useRef([0, 0]);
  const hoverRef = useRef(null);
  const lastPixelRef = useRef('');
  const [tooltip, setTooltip] = useState({ visible: false, left: 0, top: 0, fragCoord: [0,0], fragCoordNorm: [0,0], color: 'rgba(0,0,0,0)', colorNorm: [0,0,0,0] });
  const overlayRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // create sandbox and load shader
    const sandbox = new GlslCanvas(canvas);
    sandboxRef.current = sandbox;
    sandbox.load(fragShader);

    // Set initial uniforms
    sandbox.setUniform("u_time", 0);

    // Resize helper: make canvas drawing buffer match layout size * DPR
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(rect.width * dpr));
      const h = Math.max(1, Math.round(rect.height * dpr));
      // set backing buffer size
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      // keep CSS size consistent
      canvas.style.width = rect.width + "px";
      canvas.style.height = rect.height + "px";
      // inform shader of resolution in pixels (backing buffer)
      sandbox.setUniform("u_resolution", [canvas.width, canvas.height]);
      // resize overlay drawing canvas if present
      const overlay = overlayRef.current;
      if (overlay) {
        overlay.width = canvas.width;
        overlay.height = canvas.height;
        overlay.style.width = rect.width + "px";
        overlay.style.height = rect.height + "px";
      }
    };

    // Pointer mapping: map client coordinates to canvas pixel coordinates
    const onPointerMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      // if pointer outside canvas, hide tooltip
      if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
        hoverRef.current = null;
        setTooltip(t => ({ ...t, visible: false }));
        return;
      }
      // scale from CSS pixels to drawing buffer pixels
      const scaleX = canvas.width / rect.width || 1;
      const scaleY = canvas.height / rect.height || 1;
      const x = (e.clientX - rect.left) * scaleX;
      const y = (e.clientY - rect.top) * scaleY;
      // WebGL's gl_FragCoord origin is bottom-left; invert Y so shaders expecting that origin get correct coords
      const yInverted = canvas.height - y;
      // store pixel coordinates; uniforms will be set in the frame loop (keeps timing consistent)
      mouseRef.current = [x, yInverted];
  // store hover info for readback (use client coords for tooltip placement)
  hoverRef.current = { x, y: yInverted, left: e.clientX + 12, top: e.clientY + 12 };
  // reset lastPixel so we force an update
  lastPixelRef.current = '';
  // calculate normalized fragCoord (0..1)
  const fx = Math.floor(x);
  const fy = Math.floor(yInverted);
  const fnx = canvas.width > 0 ? fx / canvas.width : 0;
  const fny = canvas.height > 0 ? fy / canvas.height : 0;
  setTooltip(t => ({ ...t, visible: true, fragCoord: [fx, fy], fragCoordNorm: [fnx, fny] }));
    };

    const onPointerLeave = () => {
      hoverRef.current = null;
      setTooltip(t => ({ ...t, visible: false }));
    };

    // animation loop: update u_time
    startRef.current = performance.now();
    const frame = (t) => {
      const seconds = (t - startRef.current) / 1000;
      sandbox.setUniform("u_time", seconds);

      // compute normalized mouse (0..1) for shaders
      const mousePx = mouseRef.current.slice();
      const mouseNorm = [
        canvas.width > 0 ? mousePx[0] / canvas.width : 0,
        canvas.height > 0 ? mousePx[1] / canvas.height : 0,
      ];

      // set both normalized and pixel mouse uniforms
      sandbox.setUniform("u_mouse", mouseNorm);
      sandbox.setUniform("u_mouse_px", mousePx);

  // call optional onFrame callback with live values
      if (typeof onFrame === 'function') {
        try {
          onFrame({
            time: seconds,
            resolution: [canvas.width, canvas.height],
            mouse: mousePx,
            mouseNorm: mouseNorm,
          });
        } catch (err) {
          // swallow user callback errors
        }
      }

      // If pointer is hovering over canvas, try to read the pixel color at that location
      if (hoverRef.current) {
        try {
          const hover = hoverRef.current; // snapshot so it can't become null mid-block
          const gl = canvas.getContext('webgl2') || canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
          if (gl && hover) {
            const px = Math.floor(hover.x);
            const py = Math.floor(hover.y);
            const w = canvas.width;
            const h = canvas.height;
            if (px >= 0 && px < w && py >= 0 && py < h) {
              const buf = new Uint8Array(4);
              // readPixels expects x,y with origin bottom-left — our y is already inverted accordingly
              gl.readPixels(px, py, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, buf);
              const r = buf[0], g = buf[1], b = buf[2], a = buf[3] / 255;
              const rgba = `rgba(${r},${g},${b},${a})`;
              if (rgba !== lastPixelRef.current) {
                lastPixelRef.current = rgba;
                const colorNorm = [r / 255, g / 255, b / 255, a];
                const fragNorm = [px / canvas.width, py / canvas.height];
                setTooltip(t => ({ ...t, color: rgba, colorNorm, fragCoord: [px, py], fragCoordNorm: fragNorm, left: hover.left, top: hover.top }));
              }
            }
          }
        } catch (e) {
          // readPixels may throw if context lost or not available; ignore silently
        }
      }

      // draw crosshair on overlay when hovering
      try {
        const overlay = overlayRef.current;
        if (overlay) {
          const ctx = overlay.getContext('2d');
          // clear previous
          ctx.clearRect(0, 0, overlay.width, overlay.height);
          if (hoverRef.current) {
            const px = hoverRef.current.x;
            const pyBottom = hoverRef.current.y; // bottom-left origin
            // convert to top-left Y for 2D canvas
            const py = overlay.height - pyBottom;
            const x = px;

            ctx.save();
            ctx.lineWidth = 1;
            ctx.strokeStyle = 'rgba(255,255,255,0.4)';

            ctx.beginPath();
            ctx.moveTo(0, py + 0.5);
            ctx.lineTo(overlay.width, py + 0.5);
            
            ctx.moveTo(x + 0.5, 0);
            ctx.lineTo(x + 0.5, overlay.height);

            ctx.stroke();
            ctx.restore();
          }
        }
      } catch (e) {
        // ignore overlay drawing errors
      }

      rafRef.current = requestAnimationFrame(frame);
    };

  // initial setup
  resize();
  window.addEventListener("resize", resize);
  // attach pointer events to the canvas element for accurate hover behavior
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerleave', onPointerLeave);
  rafRef.current = requestAnimationFrame(frame);

    return () => {
      window.removeEventListener("resize", resize);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerleave', onPointerLeave);
      // clear overlay on teardown
      const overlay = overlayRef.current;
      if (overlay) {
        const ctx = overlay.getContext('2d');
        ctx.clearRect(0, 0, overlay.width, overlay.height);
      }
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (sandboxRef.current) sandboxRef.current.destroy();
    };
  }, [fragShader]);

  // Render an auto-sized canvas; let JS set backing buffer size
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <canvas ref={canvasRef} style={{ width: 400, height: 400, display: 'block' }} />
      <canvas ref={overlayRef} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }} />

      {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.left,
            top: tooltip.top,
            background: 'rgba(255,255,255,0.95)',
            color: '#000',
            padding: '6px 8px',
            borderRadius: 6,
            pointerEvents: 'none',
            fontSize: 12,
            zIndex: 9999,
            border : "1px solid rgba(0, 0, 0, 0.04)"
          }}
        >
          <div style={{ display: 'flex', flexDirection : "column", gap: "8px" }}>

            <div style={{ width: 24, height: 24, background: tooltip.color, border: '1px solid #ddd', marginRight: 8 }} />

            <div>
              <p className = "meta">Position</p>
              <div>( {tooltip.fragCoordNorm[0].toFixed(1)}, {tooltip.fragCoordNorm[1].toFixed(1)} )</div>
              <div style = {{ color: 'var(--black40)' }}>( {tooltip.fragCoord[0]}, {tooltip.fragCoord[1]} )</div>
            </div>

            <div>
              <p className = "meta">Color</p>
              <div>( {tooltip.colorNorm[0].toFixed(1)}, {tooltip.colorNorm[1].toFixed(1)}, {tooltip.colorNorm[2].toFixed(1)}, {tooltip.colorNorm[3].toFixed(1)} )</div>
              <div style = {{ color: 'var(--black40)' }}>{tooltip.color.slice(4)}</div>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default ShaderCanvas;